/**
 * permissions.js — client-side capability checks.
 *
 * Enforcement is authoritative on the SERVER (see server/tierConfig.js). The client
 * never recomputes tiers; it only reads the server-provided `capabilities` list and
 * checks membership. Everything below the `can` helper is presentational copy
 * (labels / hints for the UI) — not rules.
 */
import { appConfig } from '../app/app.config';

// v1.5 tiers (server-enforced; the client only reads the capabilities list):
//   tier 1 (Starter):      join jams + team management + host the Tutorial jam
//   tier 2 (Jammer):       completed Tutorial jam + in a team -> host the Starter jam
//   tier 3 (Host):         used Canvas set-up + hosted a Starter jam (2+ players, +3 actions) -> host any jam
//   tier 4 (Deck Creator): completed the Brainstorm jam -> Deck Builder
//   tier 5 (Builder):      published a complete deck -> Jam Builder (create/copy templates)
const BYPASS_TIERS = false;

// Does the user hold this capability? `capabilities` is the server-computed list
// returned on the User type (e.g. from DASHBOARD_QUERY / USER_EVALUATE_TIER).
export const can = (capability, capabilities) => {
    if (BYPASS_TIERS) return true; // flip to true to unlock everything for everyone
    return Array.isArray(capabilities) && capabilities.includes(capability);
};

// ---- Presentational only (server is the source of truth for enforcement) ----

// Friendly guidance shown when a capability is locked, so we can tell the user what
// to do next instead of a silent grayed-out control.
export const CAPABILITY_HINTS = {
    join_game:            "Create an account to join jams.",
    team_management:      "Create an account to manage teams.",
    host_tutorial:        "Create an account to host the Tutorial jam.",
    create_game_starter:  "Complete the Tutorial jam and join a team to host the Starter jam.",
    create_game_basic:    "Host a Starter jam with 2+ players (and try Canvas set-up) to host all jams.",
    create_game_advanced: "Host a Starter jam with 2+ players (and try Canvas set-up) to host all jams.",
    access_deckbuilder:   "Complete the Brainstorm jam (reach round 5 with 2+ players) to use the Deck Builder.",
    create_template:      "Publish a complete deck in the Deck Builder to unlock the Jam Builder.",
};

export const hintFor = (capability) =>
    CAPABILITY_HINTS[capability] || "You don't have access to this feature yet.";

// Ordered tier descriptors for the dashboard progress UI. Each tier has a display `name`
// and a list of `criteria` ids (ALL must be satisfied to reach the tier — mirrors the
// server's TIERS). Criterion ids match what the server records in `satisfiedCriteria`.
export const TIER_STEPS = [
    { tier: 1, name: "Starter",      criteria: ["account_created"] },
    { tier: 2, name: "Jammer",       criteria: ["tutorial_complete", "team_member"] },
    { tier: 3, name: "Host",         criteria: ["canvas_setup_used", "starter_host"] },
    { tier: 4, name: "Deck Creator", criteria: ["brainstorm_master"] },
    { tier: 5, name: "Builder",      criteria: ["deck_published"] },
];

// Short human label for each criterion (shown per-criterion in the progress tooltip).
export const CRITERION_LABELS = {
    account_created:   "Create an account",
    tutorial_complete: "Complete the Tutorial jam",
    team_member:       "Join or create a team",
    canvas_setup_used: "Try Canvas set-up (Setup Cards)",
    starter_host:      "Host a Starter jam (2+ players)",
    brainstorm_master: "Complete the Brainstorm jam",
    deck_published:    "Publish a complete deck",
};
export const labelFor = (criterionId) => CRITERION_LABELS[criterionId] || criterionId;

// True if the user has recorded the given criterion id.
export const hasCriterion = (criterionId, satisfiedCriteria) =>
    Array.isArray(satisfiedCriteria) &&
    satisfiedCriteria.some((c) => (c && c.id ? c.id : c) === criterionId);

// Sub-tasks that make up a challenge-gated criterion, for the dashboard progress tooltip.
// `progress` is the user's challengeProgress record: { furthest:{phase,turnStruct}, peakParticipants }.
// Presentational only — the server decides completion (see server/challengeConfig.js).
export const CRITERION_SUBTASKS = {
    tutorial_complete: [
        {
            label: "Reach round 5 in the Tutorial jam",
            value: (p) => `${(p && p.furthest && p.furthest.phase) || 0} / 5`,
            done:  (p) => ((p && p.furthest && p.furthest.phase) || 0) >= 5,
        },
    ],
    starter_host: [
        {
            // Only the host's game ever produces a starter_host progress record, so a
            // non-null progress here means the user is hosting the Starter jam.
            label: "Host a Starter jam (as the creator)",
            value: () => "",
            done:  (p) => !!p,
        },
        {
            label: "Host a jam with one other jammer using this template",
            value: (p) => `${(p && p.peakParticipants) || 0} / 2`,
            done:  (p) => ((p && p.peakParticipants) || 0) >= 2,
        },
        {
            label: "Assign a category to a card",
            value: () => "",
            done:  (p) => !!(p && Array.isArray(p.actionsSeen) && p.actionsSeen.indexOf("assign_category") !== -1),
        },
        {
            label: "Send a card to the library",
            value: () => "",
            done:  (p) => !!(p && Array.isArray(p.actionsSeen) && p.actionsSeen.indexOf("add_to_library") !== -1),
        },
        {
            label: "Add a card from the library to the canvas",
            value: () => "",
            done:  (p) => !!(p && Array.isArray(p.actionsSeen) && p.actionsSeen.indexOf("add_from_library_to_canvas") !== -1),
        },
    ],
    brainstorm_master: [
        {
            label: "Reach round 5 in a Brainstorm jam",
            value: (p) => `${(p && p.furthest && p.furthest.phase) || 0} / 5`,
            done:  (p) => ((p && p.furthest && p.furthest.phase) || 0) >= 5,
        },
        {
            label: "Host a jam with one other jammer using this template",
            value: (p) => `${(p && p.peakParticipants) || 0} / 2`,
            done:  (p) => ((p && p.peakParticipants) || 0) >= 2,
        },
    ],
    // Deck-publish conditions are evaluated all-at-once when the deck is published (no live
    // partial progress), so these flip to ✓ together once the criterion is earned.
    deck_published: [
        { label: "A card with a category",     value: () => "", done: () => false },
        { label: "A category with a color",    value: () => "", done: () => false },
        { label: "A card with content",        value: () => "", done: () => false },
        { label: "A card with a placeholder",  value: () => "", done: () => false },
    ],
};

// The template a criterion's live progress belongs to. The single challengeProgress slot
// only reflects the user's most recent attempt, so the dashboard shows live numbers only
// when this id matches challengeProgress.builderID (else it shows baseline). Per-deployment
// via appConfig.
export const CRITERION_TARGET = {
    tutorial_complete: appConfig.tutorialTemplateId,
    starter_host:      appConfig.starterTemplateId,
    brainstorm_master: appConfig.brainstormTemplateId,
};

// The sub-tasks for a criterion (empty when it has none — e.g. account_created).
export const subtasksFor = (criterionId) => CRITERION_SUBTASKS[criterionId] || [];

// The template id whose challengeProgress applies to this criterion (null if none).
export const targetFor = (criterionId) => CRITERION_TARGET[criterionId] || null;
